import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../software/api";

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;
export const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none";
export const Lbl = ({ children }) => <label className="block text-xs font-medium text-slate-600 mb-1">{children}</label>;

function useClickAway(open, close) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, close]);
  return ref;
}

// Searchable client picker used across Service / Quotes
export function ClientSelect({ value, onChange, testid }) {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useClickAway(open, () => setOpen(false));
  useEffect(() => { api("GET", "/clients").then(setClients).catch(() => {}); }, []);
  const sel = clients.find((c) => c.id === value);
  const filtered = clients.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid} className={`${inp} text-left ${sel ? "text-slate-800" : "text-slate-400"}`}>
        {sel ? sel.name : "Select client…"}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg p-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm mb-1 outline-none focus:border-sky-400" />
          <div className="max-h-48 overflow-y-auto">
            {filtered.slice(0, 40).map((c) => (
              <button type="button" key={c.id} onClick={() => { onChange(c); setOpen(false); setQ(""); }} className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-slate-50 text-sm">
                <span className="font-medium text-slate-800">{c.name}</span> <span className="text-xs text-slate-400">{c.phone}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-xs text-slate-400 px-2 py-1.5">No match.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// Same part matching as the server search: every word must appear in the name / part no / brand / supplier
// (any order), an exact barcode always matches, and names starting with the first word come first.
export function rankParts(products, q, limit) {
  const terms = (q || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return limit ? products.slice(0, limit) : products;
  const bc = q.trim();
  const hit = products.filter((p) => p.barcode === bc || terms.every((t) => `${p.name || ""} ${p.sku || ""} ${p.brand || ""} ${p.supplier || ""}`.toLowerCase().includes(t)));
  const f = terms[0], rank = (p) => { const n = (p.name || "").toLowerCase(); return p.barcode === bc ? 0 : n.startsWith(f) ? 1 : n.split(/\s+/).some((w) => w.startsWith(f)) ? 2 : 3; };
  const out = hit.map((p, i) => [rank(p), i, p]).sort((a, b) => a[0] - b[0] || a[1] - b[1]).map((x) => x[2]);
  return limit ? out.slice(0, limit) : out;
}

// Searchable product picker (adds a line via onPick)
export function ProductPicker({ onPick, testid, label = "Add item from stock" }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useClickAway(open, () => setOpen(false));
  // only the newest answer is shown, so results always match the letters typed
  useEffect(() => {
    if (!open) return; let live = true;
    const t = setTimeout(() => api("GET", `/products?${q ? `q=${encodeURIComponent(q)}&` : ""}limit=60`).then((r) => live && setProducts(r || [])).catch(() => live && setProducts([])), 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid} className="text-xs font-semibold text-sky-600 hover:text-sky-700">+ {label}</button>
      {open && (
        <div className="absolute z-30 mt-1 w-72 rounded-xl border border-slate-200 bg-white shadow-lg p-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stock…" className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm mb-1 outline-none focus:border-sky-400" />
          <div className="max-h-48 overflow-y-auto">
            {products.map((p) => (
              <button type="button" key={p.id} onClick={() => { onPick(p); setOpen(false); setQ(""); }} className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-slate-50 text-sm flex justify-between">
                <span className="text-slate-800 truncate">{p.name}</span><span className="font-mono2 text-slate-500">{money(p.price)}</span>
              </button>
            ))}
            {products.length === 0 && <p className="text-xs text-slate-400 px-2 py-1.5">No products.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export const STATUS_STYLE = {
  booked: "bg-sky-100 text-sky-700", in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700", invoiced: "bg-indigo-100 text-indigo-700",
  paid: "bg-emerald-600 text-white", cancelled: "bg-slate-200 text-slate-500",
  draft: "bg-slate-100 text-slate-600", sent: "bg-sky-100 text-sky-700",
  accepted: "bg-emerald-100 text-emerald-700", converted: "bg-indigo-100 text-indigo-700", declined: "bg-rose-100 text-rose-700",
};
